import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import filter from 'lodash/filter';
import map from 'lodash/map';

import { formatUrl } from 'url-lib';
import interpolate from 'js-utils/interpolate';

import { getLocationInfo } from './venue';

import { AFF_CODE_PREFIX } from '../constants/analytics';
import {
    SMALL_CITY_FALLBACK,
    LIKED_EVENTS,
    ORGANIZER_ARTICLE_TYPE,
    PROFILE_TYPE,
} from '../constants/constants';

import { getFiveToFollowData } from '../__fixtures__/fiveToFollowContent';

const _getUrlWithTrackingParams = ({ url, bucketKey }) => {
    const formattedKey = bucketKey.split('_').join('');
    const aff = `${AFF_CODE_PREFIX}${formattedKey}`;

    return formatUrl(url, { aff });
};

const _getImageData = (image) => {
    /* Some events don't include an image at all. In which case the expansion is
    set to `null`. So if thats the case we shouldn't event attempt to access */
    let imageUrl;

    let edgeColor;

    let imageUrlOriginal;

    if (image) {
        if (image.url) {
            imageUrl = image.url;
        }

        if (image.edgeColor) {
            edgeColor = image.edgeColor;
        }

        if (image.original && image.original.url) {
            imageUrlOriginal = image.original.url;
        }
    }

    return { imageUrl, edgeColor, imageUrlOriginal };
};

const _getPricingData = (
    ticketAvailability,
    currencyFormat,
    eventSalesStatus,
    series,
) => {
    let isFree = false;

    let isSoldOut = false;

    let minimumTicketPrice;

    let minPrice;

    if (eventSalesStatus && !series) {
        isSoldOut = eventSalesStatus.salesStatus === 'sold_out';
    }

    if (ticketAvailability) {
        minimumTicketPrice = ticketAvailability.minimumTicketPrice;
        isFree = ticketAvailability.isFree;

        /* Only want to show the price if the event is not free */
        if (minimumTicketPrice && !isFree && minimumTicketPrice.value) {
            minPrice = {
                currencyFormat,
                minPriceValue: minimumTicketPrice.value,
                currency: minimumTicketPrice.currency,
            };
        }
    }

    return { isFree, isSoldOut, minPrice };
};

const _getVenueDetails = (primaryVenue) => {
    let venueDetails = {};

    if (primaryVenue && primaryVenue.address) {
        const {
            name: venueName,
            address: {
                city,
                latitude,
                longitude,
                localizedAreaDisplay: venueAddress,
            },
        } = primaryVenue;

        venueDetails = { venueName, venueAddress, city, latitude, longitude };
    }

    return venueDetails;
};

const _getSeriesData = (series) => {
    let repeatingInstanceCount;

    if (!isEmpty(series)) {
        const {
            counts: { currentFuture },
        } = series;

        //Because search only returns events in current_future,
        //the returned count will include the passed in event so we
        //need to subtract the event count by one
        repeatingInstanceCount = currentFuture - 1;
    }

    return repeatingInstanceCount;
};

export const eventsCardInfoLookup = ({
    bucketsContent,
    currencyFormat,
    bucketKey,
}) =>
    bucketsContent.map(
        ({
            // eslint-disable-next-line no-shadow
            name,
            url,
            ticketsBy,
            startDate,
            startTime,
            primaryVenue,
            image,
            id,
            series,
            myCollections: { collections = {} } = {},
            saves: { savedByYou } = {},
            ticketAvailability,
            eventSalesStatus,
            eventCollections = [],
        }) => {
            const { venueName, venueAddress } = _getVenueDetails(primaryVenue);
            const locationInfo = interpolate(
                getLocationInfo(venueName, venueAddress),
            );
            const eventCollectionsValues = !isEmpty(eventCollections)
                ? eventCollections
                : map(collections, 'id');

            return {
                id,
                name,
                ticketsBy,
                startDate,
                startTime,
                savedByYou,
                locationInfo,
                eventCollections: eventCollectionsValues,
                repeatingInstanceCount: _getSeriesData(series),
                url: _getUrlWithTrackingParams({ url, bucketKey }),
                ..._getPricingData(
                    ticketAvailability,
                    currencyFormat,
                    eventSalesStatus,
                    series,
                ),
                ..._getImageData(image),
            };
        },
    );

const _getEventBucketsKeys = (buckets) => {
    let bucketKeys = [];

    /* This was using a constant named EVENT_TYPE = 'any' which failed all the time because that's not a key
     * in our buckets.
     */
    bucketKeys = filter(buckets, ({ type }) => type === 'event');
    bucketKeys = map(bucketKeys, 'key');
    bucketKeys = filter(
        bucketKeys,
        (value) =>
            value !== LIKED_EVENTS ||
            value !== ORGANIZER_ARTICLE_TYPE ||
            value !== PROFILE_TYPE,
    );

    return bucketKeys;
};

export const isSmallCityFallback = ({ buckets, events }) => {
    let isSmallCity = true;

    if (!isEmpty(buckets)) {
        const keys = _getEventBucketsKeys(buckets);

        isSmallCity = includes(keys, SMALL_CITY_FALLBACK);
    } else if (!isEmpty(events)) {
        const smallCityEventsLength = 8;
        const firstEventBucketLength = events[0]
            ? get(events[0], 'events.length', 0)
            : get(events.results, 'length', 0);

        isSmallCity = firstEventBucketLength < smallCityEventsLength;
    }

    return isSmallCity;
};

export const isZeroContent = ({ buckets, events }) => {
    let response = true;

    if (!isEmpty(buckets)) {
        const keys = _getEventBucketsKeys(buckets);

        response = isEmpty(keys);
    } else if (!isEmpty(events)) {
        const firstEventBucket =
            (events[0] && events[0].events) || (events && events.results);

        response = isEmpty(firstEventBucket);
    }

    return response;
};

export const getFiveToFollowContent = (placeId = '') => {
    let fiveToFollowContent = [];

    if (placeId) {
        fiveToFollowContent = getFiveToFollowData()[placeId];
    }

    return fiveToFollowContent ? fiveToFollowContent : [];
};
